import type { CaseStudy } from '../types';

export const mcdonalds: CaseStudy = {
  id: 'mcdonalds',
  order: 1,
  title: '麦当劳',
  english: "McDonald's",
  category: 'business',
  era: '1955 — 今',
  region: '美国起源 / 全球',
  accent: '#c65a3e',
  hook: '金色拱门卖的不只是汉堡。它把一套可复制的门店系统、加盟者的资本和稀缺地段的租约叠在一起。',
  question: '为什么同样卖汉堡，有的店只能开一家，有的能开四万家？',
  summary:
    '麦当劳的规模化不是单点发明，而是标准化、规模分摊、特许经营与地产控制互相加固的结果。每一环都在制造利润，也在制造新的冲突。',
  thesis: '连锁的护城河很少来自产品本身，而来自「可复制性 × 稀缺节点 × 利益分配」的组合。',
  quote: '标准化让门店可以被复制；地产和租约让复制出来的现金流不容易被拿走。',
  entryNodeIds: ['mcd-standard', 'mcd-scale', 'mcd-franchise'],
  focusNodeId: 'mcd-franchise',
  lensIds: ['scale', 'incentives', 'standards', 'scarcity'],
  opportunityIds: ['opp-shared-backoffice', 'opp-lease-stress-test'],
  sourceIds: ['mcd-10k-2024', 'mcd-history'],
  facts: [
    {
      text: '1955 年，雷·克洛克在伊利诺伊州德斯普兰斯开设首家麦当劳加盟店；1961 年买下麦当劳兄弟的商标与系统权利。',
      sourceIds: ['mcd-history'],
    },
    {
      text: '2024 年末，麦当劳在全球约有 43,000 家门店，约 95% 由加盟者经营；收入包括自营门店销售，以及向加盟店收取的租金、权利金等。',
      sourceIds: ['mcd-10k-2024'],
    },
    {
      text: '公司没有在财报中单独披露「地产收入」的占比。「麦当劳其实是一家地产公司」是解释性说法，不是严格的财报口径。',
      sourceIds: ['mcd-10k-2024'],
    },
  ],
  nodes: [
    {
      id: 'mcd',
      caseId: 'mcdonalds',
      kind: 'case',
      stage: 0,
      lane: 0,
      title: '麦当劳',
      summary: '从一家汽车餐厅长成全球门店系统。',
      detail:
        '麦当劳兄弟在 1940 年代把汽车餐厅改造成自助式、有限菜单、快速出餐的模式；雷·克洛克在 1955 年加入并推动加盟扩张。值得拆开的不是某款汉堡，而是这套系统如何把「一家店的成功」变成「可重复的门店网络」。',
      boundary: '这个案例主要解释标准化快餐与郊区汽车文化下的连锁扩张，不适用于所有餐饮业态。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['mcd-history', 'mcd-10k-2024'],
    },
    {
      id: 'mcd-standard',
      caseId: 'mcdonalds',
      kind: 'mechanism',
      stage: 1,
      lane: -1,
      title: '标准化：把口味变成流程',
      summary: '菜单、设备、动作与检查表，让不同的人做出接近的结果。',
      detail:
        '有限菜单、专用设备、操作规范和检查表，把影响体验与成本的关键变量变成可训练、可检查的动作。门店因此可以复制，而不必依赖某位厨师的手感。标准化真正降低的是「不确定性」：培训更短、出品更稳、供应链更容易预测。',
      boundary: '当需求高度本地化、原料波动大，或创新依赖门店自主试验时，过度标准化会削弱适应力。',
      counter: '精品餐厅靠厨师判断与非标体验获得溢价，标准化反而会摧毁它的价值。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['mcd-history', 'mcd-10k-2024'],
    },
    {
      id: 'mcd-scale',
      caseId: 'mcdonalds',
      kind: 'mechanism',
      stage: 1,
      lane: 1,
      title: '规模分摊：广告与采购',
      summary: '同一笔供应链、品牌和总部投入，被更多门店与订单共同承担。',
      detail:
        '采购量上升增强议价权；全国广告基金把品牌曝光摊薄到单店；总部系统、培训和研发也由更大的网络分摊。规模经济的关键不是「大」，而是新增门店带来的边际成本低于边际收入。',
      boundary: '当管理半径、加盟商监督、物流距离或菜单复杂度增长快于营收时，规模会变成规模不经济。',
      counter: '区域连锁在本地密度高、响应快，可能在特定城市击败全国品牌。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['mcd-10k-2024'],
    },
    {
      id: 'mcd-franchise',
      caseId: 'mcdonalds',
      kind: 'mechanism',
      stage: 2,
      lane: -1,
      title: '特许经营：用别人的资本扩张',
      summary: '加盟者出资本和日常经营，总部收权利金与服务费，同时保留系统控制权。',
      detail:
        '特许经营把开店的资本开支与部分经营风险转移给加盟者，总部用较低的自有资本扩张网络。代价是总部与加盟者的目标不完全一致：总部希望网络增长和系统一致，加盟者更关心单店回本。',
      boundary: '只有当单店经济模型稳定、总部能低成本监督质量、且法律能执行合同时，加盟才可持续。',
      counter: '直营模式在体验控制、数据闭环和利润留存上更强，但扩张更吃资本。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['mcd-10k-2024'],
    },
    {
      id: 'mcd-realestate',
      caseId: 'mcdonalds',
      kind: 'mechanism',
      stage: 2,
      lane: 1,
      title: '地产与租约：抓住稀缺位置',
      summary: '总部长期租赁或持有物业，再转租给加盟者，把位置控制权变成现金流。',
      detail:
        '在麦当劳的典型安排中，总部或其地产主体取得长期租约或物业，再向加盟者收取租金；租金与权利金共同构成加盟收入。这样总部不只是品牌授权方，也掌握了退出、续约和选址的筹码。',
      boundary: '不同国家、不同年代和不同门店的物业安排差异很大；总部并不一定拥有所有门店物业。',
      counter: '把麦当劳简单等同于「地产公司」会夸大单一因素，也会忽略餐饮运营、品牌和供应链的作用。',
      evidence: 'mechanism',
      confidence: 'medium',
      sourceIds: ['mcd-10k-2024'],
    },
    {
      id: 'mcd-condition',
      caseId: 'mcdonalds',
      kind: 'condition',
      stage: 2,
      lane: 0,
      title: '前提：汽车、郊区与冷链',
      summary: '战后美国郊区化、汽车出行、公路网与冷链，让标准化快餐有稳定的需求与供给。',
      detail:
        '没有开车取餐的场景、没有稳定的牛肉、土豆和冷链供应，单店模型无法以同样方式复制。制度与基础设施决定了某个商业模式在什么时间、什么地点可行；同样的菜单搬到步行城市或外卖密度低的市场，结果会不同。',
      boundary: '把美国郊区模型直接搬到别的城市形态，会高估复制速度。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['mcd-history'],
    },
    {
      id: 'mcd-outcome',
      caseId: 'mcdonalds',
      kind: 'outcome',
      stage: 3,
      lane: 0,
      title: '结果：高密度网络与现金牛',
      summary: '系统、资本与位置互相加固，形成难以一次性复制的门店网络。',
      detail:
        '这套组合让麦当劳在很长时间内保持扩张、品牌曝光和现金流。真正难复制的是供应链、租约、加盟商网络与消费者习惯的总和，而不是某一份菜单。网络越密，广告越有效、供应链越有规模、位置越稀缺。',
      boundary: '饱和市场、健康观念变化、劳动成本上升和外卖平台抽成会侵蚀单店回报。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['mcd-10k-2024'],
    },
    {
      id: 'mcd-risk',
      caseId: 'mcdonalds',
      kind: 'risk',
      stage: 4,
      lane: -1,
      title: '脆弱处：总部与加盟者的账本不一致',
      summary: '门店利润承压时，租金、改造、降价与人工成本会在总部和加盟者之间重新分配。',
      detail:
        '总部收入相对更依赖网络规模和租金/权利金，加盟者承担单店销售波动和人工成本。遇到降价、装修升级或外卖平台抽成时，双方可能对「该谁让利」产生冲突。冲突不一定爆发，但会改变扩张速度与品牌一致性。',
      boundary: '合同设计、加盟商协会力量、当地劳动法与竞争程度，决定冲突是否失控。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['mcd-10k-2024'],
    },
    {
      id: 'mcd-opp',
      caseId: 'mcdonalds',
      kind: 'opportunity',
      stage: 4,
      lane: 1,
      title: '机会透镜：卖系统，不卖单店',
      summary: '可迁移的不是汉堡，而是「标准化交付 + 外部资本 + 稀缺节点」这套结构。',
      detail:
        '如果你做本地服务，先问：哪些环节可以变成检查表？谁愿意为成熟系统付钱？你控制的稀缺节点是位置、客户关系、供应链还是数据？不要只学一个品类，要学利润如何被分配。',
      boundary: '没有复购、没有稀缺节点、只靠加盟费赚钱的模式，容易把风险留给最弱的一环。',
      evidence: 'inference',
      confidence: 'medium',
      sourceIds: ['mcd-10k-2024'],
    },
  ],
  edges: [
    {
      id: 'mcd-e1',
      caseId: 'mcdonalds',
      from: 'mcd',
      to: 'mcd-standard',
      label: '把经验变成流程',
      explanation: '有限菜单和标准化操作，把「一家店的手艺」变成可训练、可检查的系统。',
      tone: 'cause',
    },
    {
      id: 'mcd-e2',
      caseId: 'mcdonalds',
      from: 'mcd',
      to: 'mcd-scale',
      label: '单店复制成网络',
      explanation: '门店越多，采购、广告和总部投入越能被更多单店分摊。',
      tone: 'cause',
    },
    {
      id: 'mcd-e3',
      caseId: 'mcdonalds',
      from: 'mcd-standard',
      to: 'mcd-franchise',
      label: '可核查，才可授权',
      explanation: '只有结果可测量、流程可训练，总部才敢把门店交给外部加盟者，并用检查维持一致性。',
      tone: 'enable',
    },
    {
      id: 'mcd-e4',
      caseId: 'mcdonalds',
      from: 'mcd-scale',
      to: 'mcd-franchise',
      label: '系统越强，加盟越值钱',
      explanation: '规模带来品牌、供应链和广告优势，加盟者更愿意为成熟系统付费。',
      tone: 'cause',
    },
    {
      id: 'mcd-e5',
      caseId: 'mcdonalds',
      from: 'mcd-franchise',
      to: 'mcd-realestate',
      label: '总部替加盟者拿位置',
      explanation: '门店网络需要持续选址，总部通过长期租约或物业控制，把位置稀缺性留在系统内部。',
      tone: 'cause',
    },
    {
      id: 'mcd-e6',
      caseId: 'mcdonalds',
      from: 'mcd-franchise',
      to: 'mcd-outcome',
      label: '资本外部化',
      explanation: '加盟者用自有资本开店，总部以租金和服务费分享收益，扩张不必完全占用总部资产负债表。',
      tone: 'cause',
    },
    {
      id: 'mcd-e7',
      caseId: 'mcdonalds',
      from: 'mcd-realestate',
      to: 'mcd-outcome',
      label: '位置变成现金流',
      explanation: '长期租约和转租安排，把黄金位置的稀缺性变成经常性收入与续约筹码。',
      tone: 'cause',
    },
    {
      id: 'mcd-e8',
      caseId: 'mcdonalds',
      from: 'mcd-condition',
      to: 'mcd-outcome',
      label: '没有前提，复制会失速',
      explanation: '郊区化、汽车出行、冷链和稳定供应链，是这个单店模型能高速复制的外部条件。',
      tone: 'enable',
    },
    {
      id: 'mcd-e9',
      caseId: 'mcdonalds',
      from: 'mcd-outcome',
      to: 'mcd-risk',
      label: '规模也放大冲突',
      explanation: '网络越大，总部与加盟者对降价、改造和租金的利益分歧越难靠个案协商解决。',
      tone: 'risk',
    },
    {
      id: 'mcd-e10',
      caseId: 'mcdonalds',
      from: 'mcd-franchise',
      to: 'mcd-risk',
      label: '风险下沉到最弱一环',
      explanation: '加盟者承担单店销售波动和人工成本；当利润变薄，冲突会沿合同重新分配。',
      tone: 'risk',
    },
    {
      id: 'mcd-e11',
      caseId: 'mcdonalds',
      from: 'mcd-outcome',
      to: 'mcd-opp',
      label: '结构可迁移',
      explanation: '标准化交付、外部资本与稀缺节点控制的组合，可以被迁移到许多本地服务与小生意。',
      tone: 'cause',
    },
    {
      id: 'mcd-e12',
      caseId: 'mcdonalds',
      from: 'mcd-risk',
      to: 'mcd-opp',
      label: '反向定位',
      explanation: '如果只学标准化而没有稀缺节点，门店更容易被替代；机会在于找到自己不可替代的那一环。',
      tone: 'enable',
    },
  ],
};
